import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Button,
  BoxProps,
  OutlinedInput
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import CustomTree, {
  TreeItemI
} from '../../../../../components/customTree/CustomTree'
import { useRatesDataGridContext } from '../contexts/ratesDataGrid/RatesDataGridContext'
import {
  haveSameRateFilterIds,
  normalizeRateFilterIds
} from '../RatesDataGrid.filters'

const ALL_RATES_VALUE = '__all_rates__'

const FilterBar: ReactFC<BoxProps> = ({ sx }) => {
  const {
    apiData,
    filterVisibilityId,
    changeFilterVisibility,
    filterRateIds,
    changeFilterRateIds,
    resetFilters,
    treeItems,
    changeTreeItems,
    loading
  } = useRatesDataGridContext()

  const { t } = useTranslation()

  const availableRateIds = useMemo(
    () => apiData?.rateAuxList?.map(({ id }) => id) ?? [],
    [apiData?.rateAuxList]
  )
  const ratesById = useMemo(
    () => new Map(apiData?.rateAuxList?.map((rate) => [rate.id, rate]) ?? []),
    [apiData?.rateAuxList]
  )
  const [draftRateIds, setDraftRateIds] = useState<string[]>(filterRateIds)
  const [rateMenuOpen, setRateMenuOpen] = useState(false)

  const handleChangeVisibility = async (event: SelectChangeEvent) => {
    const value = event.target.value
    changeFilterVisibility(value)
  }

  const handleChangeRatesFilter = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value
      const selectedRateIds = typeof value === 'string' ? value.split(',') : value
      if (selectedRateIds.includes(ALL_RATES_VALUE)) {
        setDraftRateIds([])
        return
      }
      setDraftRateIds(
        normalizeRateFilterIds(selectedRateIds, availableRateIds)
      )
    },
    [availableRateIds]
  )

  const handleOpenRatesFilter = useCallback(() => {
    setDraftRateIds(normalizeRateFilterIds(filterRateIds, availableRateIds))
    setRateMenuOpen(true)
  }, [availableRateIds, filterRateIds])

  const handleCloseRatesFilter = useCallback(() => {
    const normalizedRateIds = normalizeRateFilterIds(
      draftRateIds,
      availableRateIds
    )
    setDraftRateIds(normalizedRateIds)
    setRateMenuOpen(false)
    if (!haveSameRateFilterIds(filterRateIds, normalizedRateIds)) {
      changeFilterRateIds(normalizedRateIds)
    }
  }, [availableRateIds, changeFilterRateIds, draftRateIds, filterRateIds])

  useEffect(() => {
    if (rateMenuOpen) return
    setDraftRateIds(normalizeRateFilterIds(filterRateIds, availableRateIds))
  }, [availableRateIds, filterRateIds, rateMenuOpen])

  const handleChangeStablishments = async (newTreeItems: TreeItemI[]) => {
    changeTreeItems(newTreeItems)
  }

  const handleClearFilters = () => {
    setDraftRateIds([])
    resetFilters()
  }

  const initialLoad = loading && !apiData

  if (initialLoad) return null

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '6px',
        '& > *': {
          flex: '0 1 30%'
        },
        '& > *:not(:first-of-type)': {
          marginLeft: '1rem'
        },
        ...sx
      }}
    >
      <FormControl>
        <InputLabel>
          {t('apps.rates.rateDataGrid.labels.visibility')}
        </InputLabel>
        <Select
          label={`${t('apps.rates.rateDataGrid.labels.visibility')}`}
          value={filterVisibilityId || apiData?.filterList[2].id}
          onChange={handleChangeVisibility}
        >
          {apiData?.filterList.map((filter) => (
            <MenuItem value={filter.id} key={filter.id}>
              {filter.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box
        sx={{
          '& .tree__chips_container ': {
            maxHeight: '2rem',
            overflow: 'auto'
          }
        }}
      >
        <CustomTree
          selectProps={{
            fullWidth: true
          }}
          treeItems={treeItems}
          onChange={handleChangeStablishments}
        />
      </Box>
      <FormControl>
        <InputLabel shrink>
          {t('apps.rates.rateDataGrid.labels.rates')}
        </InputLabel>
        <Select
          multiple
          open={rateMenuOpen}
          value={draftRateIds}
          label={`${t('apps.rates.rateDataGrid.labels.rates')}`}
          onChange={handleChangeRatesFilter}
          onOpen={handleOpenRatesFilter}
          onClose={handleCloseRatesFilter}
          displayEmpty
          renderValue={(selectedRateIds) => {
            if (selectedRateIds.length === 0) {
              return t('apps.rates.rateDataGrid.labels.all_rates')
            }
            return (
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  overflow: 'hidden'
                }}
              >
                {selectedRateIds.map((rateId) => (
                  <Chip
                    key={rateId}
                    label={ratesById.get(rateId)?.name ?? rateId}
                    size='small'
                  />
                ))}
              </Box>
            )
          }}
          input={
            <OutlinedInput
              notched
              label={`${t('apps.rates.rateDataGrid.labels.rates')}`}
            />
          }
        >
          <MenuItem value={ALL_RATES_VALUE}>
            <Checkbox checked={draftRateIds.length === 0} />
            <ListItemText
              primary={t('apps.rates.rateDataGrid.labels.all_rates')}
            />
          </MenuItem>
          {apiData?.rateAuxList?.map((rate) => (
            <MenuItem value={rate.id} key={rate.id}>
              <Checkbox checked={draftRateIds.includes(rate.id)} />
              <ListItemText primary={rate.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box
        sx={{
          width: '20%'
        }}
      >
        <Button onClick={handleClearFilters}>
          {t('apps.rates.rateDataGrid.labels.clearFilters')}
        </Button>
      </Box>
    </Box>
  )
}
export default FilterBar
